import { inject, Injectable } from '@angular/core';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { ApiRoutePackingBoxService } from '../../../api/elite/routes/packing-box.service';
import { PackingBoxDto } from '../../../api/elite/dto/packing-box.dto';
import { ApiRouteTransactionDto } from '../../../api/elite/dto/purchase-order.dto';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { ApiNtAssemblyItemService } from '../../../api/netsuite/routes/assembly-item.service';
import { ApiRouteTransactionService } from '../../../api/elite/routes/transaction.service';
import { WebSocketApiLabelService } from '../../../api/elite/websockets/label.service';
import { ApiNtWorkOrderService } from '../../../api/netsuite/routes/work-order.service';
import { ApiRouteLotNumberService } from '../../../api/elite/routes/lot-number.service';
import { Transaction } from '../../../api/elite/types/transaction';
import {
  PackingBoxTablePetitionRow,
  PackingBoxTableRow,
} from '../../../common/packing-box/models/table-row';
import { WebSocketLabelDto } from '../../../api/elite/dto/ws-label.dto';
import { ZebraService } from '../../../core/services/zebra.service';
import { AlertService } from '../../../core/services/alert.service';
import { ViewApiService } from './view-api.service';

/**
 * Packing-box view API.
 *
 * NOTE: As of the Zebra-only cleanup, this service ONLY supports printing
 * labels through Zebra printers using ZPL codes. The previous local-printer
 * (non-Zebra) path that called `ApiLocalPortLabelService.printLabel(...)`
 * was removed. All work-order labels are now generated as ZPL on the
 * backend (`ApiFilesBarCodeService` via `ViewApiService.printLabel`) and
 * sent to the Zebra printer through `ZebraService`.
 */
@Injectable()
export class PackingBoxViewApiService {
  protected readonly apiPackingBoxRouter = inject(ApiRoutePackingBoxService);
  protected readonly netsuiteAssemblyItemRouter = inject(
    ApiNtAssemblyItemService
  );
  protected readonly nsWorkOrderRouter = inject(ApiNtWorkOrderService);
  protected readonly apiTransactionRouter = inject(ApiRouteTransactionService);
  protected readonly webSocket = inject(WebSocketApiLabelService);
  protected readonly apiLotNumberRouter = inject(ApiRouteLotNumberService);
  protected readonly zebraService = inject(ZebraService);
  protected readonly alertService = inject(AlertService);
  protected readonly zebraLocalHostLabelRouter = inject(ViewApiService);

  constructor() {}

  data(title: string): Observable<PackingBoxTableRow[]> {
    return this.apiPackingBoxRouter.traceabilityData(title);
  }

  dataToCreatePetition(
    department: string
  ): Observable<PackingBoxDto['dataToCreatePetition']['response']> {
    return this.apiPackingBoxRouter.dataToCreatePetition(department);
  }

  createPetition(
    body: PackingBoxDto['createPetition']['body']
  ): Observable<any> {
    return this.apiPackingBoxRouter.createPetition(body);
  }

  getDataOfPetitions(
    department: string
  ): Observable<PackingBoxTablePetitionRow[]> {
    return this.apiPackingBoxRouter.openPetitions(department);
  }

  getPrioritySaleOrderLine(
    lineId?: number,
    assemblyItemId?: number
  ): Observable<{
    salesOrder: string;
    partNumberByCustomer: string;
    salesOrderLineId?: number;
    salesOrderToInsert?: ApiRouteTransactionDto['insert']['body'];
  }> {
    if (lineId) {
      return this.apiTransactionRouter.getLine(lineId).pipe(
        map((res) => {
          return {
            salesOrderLineId: res.id,
            salesOrder: res.transaction.title,
            quantity: res.quantityAsked - res.quantityFinished,
            partNumberByCustomer:
              res.salesOrderLineDetails.partNumberByCustomer,
          };
        })
      );
    }
    return this.netsuiteAssemblyItemRouter.openSalesOrders(assemblyItemId).pipe(
      switchMap((salesOrders) => {
        const saleOrderIds = salesOrders.map((o) => o.saleOrderId);

        return this.apiTransactionRouter
          .getSeveral({ specificIds: saleOrderIds })
          .pipe(
            map((secondList) => {
              const transactionMap = new Map(
                secondList.map((t) => [t.transactionId, t])
              );

              const validOrders = salesOrders.filter((order) => {
                const transaction = transactionMap.get(order.saleOrderId);

                return !(
                  transaction &&
                  Array.isArray(transaction.lines) &&
                  transaction.lines.length === 0
                );
              });

              return validOrders.sort(
                (a, b) =>
                  new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
              )[0];
            })
          );
      }),
      map((salesOrder) => ({
        salesOrder: salesOrder.saleOrder,
        partNumberByCustomer: salesOrder.partNumberByCustomer,
        quantity: salesOrder.quantityAsked,
        salesOrderToInsert: {
          dueDate: salesOrder.dueDate,
          title: salesOrder.saleOrder,
          transactionId: salesOrder.saleOrderId,
          typeOfTransaction: 'Sales Order',
          salesOrderDetails: {
            customerId: salesOrder.customerId,
            customerName: salesOrder.customerName,
          },
          line: {
            lineId: salesOrder.transactionLineId,
            quantityAsked: salesOrder.quantityAsked,
            salesOrderLineDetails: {
              partNumberByCustomer: salesOrder.partNumberByCustomer,
            },
            assemblyItem: {
              title: salesOrder.itemName,
              itemId: salesOrder.itemId,
              upc: '',
              description: salesOrder.itemName,
              classId: 0,
              specialInstructions: '',
            },
          },
        },
      }))
    );
  }

  protected canStationPrinter(
    currentDepartment: string,
    printerNameStation: string,
    printerNameLabel: string
  ): boolean {
    const departments = [
      'polyurethane',
      'filter',
      'pleat packs',
      'hoses',
      'compounding',
      'injection molding',
      'plastics',
    ];

    const departmentHasCustomPrinter = departments.includes(
      currentDepartment.toLowerCase()
    );

    if (!departmentHasCustomPrinter) {
      return true;
    }

    return printerNameStation === printerNameLabel;
  }

  printBasicWorkOrders(
    brand: 'sb' | 'daystar',
    department: string,
    printerName?: string
  ): Observable<any> {
    return this.webSocket.listToPrintLabels().pipe(
      tap((res) => console.log(res, 'from socket')),
      filter(
        (res) =>
          res.data.department === department &&
          this.canStationPrinter(department, printerName, res.data.namePrinter)
      ),
      switchMap((res) => {
        console.log(`Label received `, res);
        switch (res.type) {
          case 'wo-basic':
            return this.handleWoLabels(brand, res.data);

          case 'zpl':
            return this.handleZplLabels(res.data.zpl);
        }
      })
    );
  }

  closeConnection(): void {
    return this.webSocket.closeConnection();
  }

  getSpecificLotNumber(
    workOrder: string
  ): Observable<{ lot: string; hasInventoryNumber: boolean }> {
    return this.nsWorkOrderRouter.getBom(workOrder).pipe(
      switchMap((bom) => {
        const withInventoryNumber = bom.bom.filter((el) => el.inventoryNumber);
        const hasInventoryNumber = withInventoryNumber.length > 0;
        return this.apiLotNumberRouter
          .lotNumber(workOrder)
          .pipe(map((lot) => ({ lot: lot?.title, hasInventoryNumber })));
      })
    );
  }

  addSalesOrders(
    list: ApiRouteTransactionDto['insert']['body']
  ): Observable<Transaction> {
    return this.apiTransactionRouter.insert(list);
  }

  itemsIsUsedOn(item: string): Observable<string[]> {
    return this.netsuiteAssemblyItemRouter.getChildItemsIsUsedOn(item);
  }

  /**
   * Handles "wo-basic" WebSocket label messages by requesting the ZPL
   * representation of the label from the backend and then printing it on the
   * local Zebra printer. The legacy non-Zebra path (which used to send the
   * label data directly to a non-ZPL local printer driver) has been removed.
   */
  protected handleWoLabels(
    brand: 'sb' | 'daystar',
    res: WebSocketLabelDto['listToPrintBasicWorkOrderLabel']['result']
  ): Observable<any> {
    return this.zebraLocalHostLabelRouter
      .printLabel({
        type: 'packing-box-wip',
        quantity: res.quantity,
        part_number_elite: res.itemName,
        part_number_client: '',
        description: res.itemDescription,
        sales_order: '',
        ids: Array(res.quantityOfLabels).fill(1),
        legacy_ids: [],
        lot: '',
        po: '',
        batch: res.batch,
        work_order: res.workOrder,
        brand,
        corte: res.trimming,
      })
      .pipe(
        switchMap((response) => this.handleZplLabels(response)),
        catchError(() => of(undefined)),
        switchMap(() => of(false))
      );
  }

  /**
   * Sends an array of ZPL strings to the local Zebra printer.
   */
  protected handleZplLabels(response: string[]): Observable<string[]> {
    return forkJoin(
      response.map((zpl) =>
        from(this.zebraService.initPrinter()).pipe(
          switchMap(() => from(this.zebraService.printZpl(zpl, 1))),
          catchError((err) => {
            this.alertService.error(
              err,
              'Error al inicializar la impresora Zebra'
            );
            return EMPTY;
          })
        )
      )
    ).pipe(map(() => response));
  }
}
